import { _ } from "i18n";
import { useEffect, useMemo, useState } from "react";
import { type ButtonTabsItem } from "ui-kit";
import { SelectValue } from "ui-kit/src/MultipleSelect/types";
import { GamePageResponse } from "../../types";
import { getTaggedTeamSide, isOneTeamTagged } from "../../utils/teamTagged";
import OneTeamTaggingDisclaimer from "../OneTeamTaggingDisclaimer";
import CategoryTabs from "./CategoryTabs";
import FilterBar from "./FilterBar";
import { CustomTab } from "./constants";
import { findScrollParent } from "./utils/scrollUtils";

interface InsightHeaderProps {
	gameData: GamePageResponse;
	categoryTabs: CustomTab[];
	customTabs: CustomTab[];
	userCustomTabs: CustomTab[];
	selectedDropdownCategory: SelectValue | undefined;
	onDropdownCategoryChange: (selected: SelectValue) => void;
	onEditTab: () => void;
	onAddTab: () => void;
	team: number | null;
	onTeamChange: (teamId: number) => void;
	selectedPlayer: any;
	onPlayerChange: (player: any) => void;
	playerListByTeam: any;
	periodTabs: any[];
	periods: string[];
	onPeriodsChange: (periods: (string | number)[]) => void;
	showPlayerNumber: boolean;
	onShowPlayerNumberChange: () => void;
	sentinelRef: React.RefObject<HTMLDivElement>;
	filterContainerRef: React.RefObject<HTMLDivElement>;
}

const InsightHeader = ({
	gameData,
	categoryTabs,
	customTabs,
	userCustomTabs,
	selectedDropdownCategory,
	onDropdownCategoryChange,
	onEditTab,
	onAddTab,
	team,
	onTeamChange,
	selectedPlayer,
	onPlayerChange,
	playerListByTeam,
	periodTabs,
	periods,
	onPeriodsChange,
	showPlayerNumber,
	onShowPlayerNumberChange,
	sentinelRef,
	filterContainerRef,
}: InsightHeaderProps) => {
	const [isNarrow, setIsNarrow] = useState(false);
	const [isSticky, setIsSticky] = useState(false);
	const homeClub = gameData.homeGameTeam;
	const awayClub = gameData.awayGameTeam;
	const taggedSide = getTaggedTeamSide(gameData.game?.teamTagged);
	const isOtt = isOneTeamTagged(gameData.game?.teamTagged);

	const clubs: ButtonTabsItem[] = useMemo(() => {
		const clubsList: ButtonTabsItem[] = [];
		if (homeClub && (isOtt === false || taggedSide === "home")) {
			clubsList.push({
				key: homeClub.id,
				label: _("label.match.homeTeam"),
				image: homeClub.logo,
				imageShape: "round",
			});
		}
		if (awayClub && (isOtt === false || taggedSide === "away")) {
			clubsList.push({
				key: awayClub.id,
				label: _("label.match.awayTeam"),
				image: awayClub.logo,
				imageShape: "round",
			});
		}
		return clubsList;
	}, [homeClub, awayClub, isOtt, taggedSide]);

	useEffect(() => {
		const checkWidth = () => {
			setIsNarrow(window.innerWidth <= 1140);
		};
		checkWidth();
		window.addEventListener("resize", checkWidth);
		return () => window.removeEventListener("resize", checkWidth);
	}, []);

	// Sticky header: only when sentinel is out of view AND user has scrolled past top (forbid sticky at top of page)
	// Resolve scroll container: Discover uses [data-discover-scroll], otherwise find overflow parent, else window
	const STICKY_OBSERVER_DELAY_MS = 300;
	const STICKY_ACTIVATE_AFTER_MS = 80;
	const SCROLL_TOP_THRESHOLD_PX = 10;
	useEffect(() => {
		const el = sentinelRef.current;
		if (!el) return;

		const scrollEl = document.querySelector<HTMLElement>("[data-discover-scroll]") ?? findScrollParent(el) ?? null;
		const useWindowScroll = !scrollEl;

		let observeTimeoutId: ReturnType<typeof setTimeout>;
		let activateTimeoutId: ReturnType<typeof setTimeout> | undefined;

		const updateSticky = () => {
			const rect = el.getBoundingClientRect();
			const sentinelOutOfView = rect.bottom < 0;
			const scrollTop = useWindowScroll ? window.scrollY : (scrollEl as HTMLElement).scrollTop;
			setIsSticky(sentinelOutOfView && scrollTop > SCROLL_TOP_THRESHOLD_PX);
		};

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					if (activateTimeoutId) {
						clearTimeout(activateTimeoutId);
						activateTimeoutId = undefined;
					}
					setIsSticky(false);
				} else {
					activateTimeoutId = setTimeout(() => {
						activateTimeoutId = undefined;
						updateSticky();
					}, STICKY_ACTIVATE_AFTER_MS);
				}
			},
			{
				threshold: 0,
				rootMargin: "-1px 0px 0px 0px",
			}
		);

		observeTimeoutId = setTimeout(() => {
			observer.observe(el);
		}, STICKY_OBSERVER_DELAY_MS);

		// Scroll listener on actual scroll container (window when doc scrolls, div when Discover layout)
		const scrollTarget = useWindowScroll ? window : (scrollEl as HTMLElement);
		scrollTarget.addEventListener("scroll", updateSticky, { passive: true });

		return () => {
			clearTimeout(observeTimeoutId);
			if (activateTimeoutId) clearTimeout(activateTimeoutId);
			observer.disconnect();
			scrollTarget.removeEventListener("scroll", updateSticky);
		};
	}, [sentinelRef]);

	return (
		<>
			{/* Sentinel marker for sticky header detection - 1px height ensures reliable IntersectionObserver */}
			<div ref={sentinelRef} style={{ height: 1 }} aria-hidden="true" />
			<div ref={filterContainerRef}>
				<FilterBar
					clubs={clubs}
					team={team}
					onTeamChange={onTeamChange}
					selectedPlayer={selectedPlayer}
					onPlayerChange={onPlayerChange}
					playerListByTeam={playerListByTeam}
					periodTabs={periodTabs}
					periods={periods}
					onPeriodsChange={onPeriodsChange}
					showPlayerNumber={showPlayerNumber}
					onShowPlayerNumberChange={onShowPlayerNumberChange}
					isSticky={isSticky}
					isNarrow={isNarrow}
					hideTeamSwitcher={clubs.length <= 1}
				/>
			</div>
			<CategoryTabs
				categoryTabs={categoryTabs}
				customTabs={customTabs}
				userCustomTabs={userCustomTabs}
				selectedDropdownCategory={selectedDropdownCategory}
				onDropdownCategoryChange={onDropdownCategoryChange}
				onEditTab={onEditTab}
				onAddTab={onAddTab}
			/>
			{isOtt && <OneTeamTaggingDisclaimer />}
		</>
	);
};

export default InsightHeader;
